import { useRef, useEffect } from "preact/hooks";
import {
  listItems,
  listLoading,
  listHasMore,
  listError,
  listEpoch,
  listScrollRequest,
  listRefetching,
} from "../state/store";
import { loadMoreItems } from "../data/queries";
import { ConcertCard } from "./ConcertCard";
import { HistoricalConcertCard } from "./HistoricalConcertCard";

/** Where the list starts: the top of the sticky count bar. */
function listTopOffset(): number | null {
  const bar = document.querySelector(".count-bar");
  if (!(bar instanceof HTMLElement)) return null;
  return bar.getBoundingClientRect().top + window.scrollY;
}

/** Scroll the window back to the list top, but only when the reader is already below it —
 *  a filter tweak made at the top of the page shouldn't yank the viewport down. */
function scrollListTop() {
  const top = listTopOffset();
  if (top === null) return;
  if (window.scrollY > top) window.scrollTo({ top });
}

/** Scroll the list into view unconditionally — including downwards. Only for explicit
 *  "show me these" actions taken away from the list (the map's city popup); the passive
 *  post-filter case above must keep its upward-only guard. */
function scrollListIntoView() {
  const top = listTopOffset();
  if (top === null) return;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
}

/** THE reading list: live concerts and archive events interleaved chronologically by
 *  data/queries.ts's merge. Each row renders its own card shape — the two kinds carry
 *  different epistemics (datetime + tickets link vs fuzzy date + citation) on one shell. */
export function UnifiedList() {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const items = listItems.value;
  const loading = listLoading.value;
  const hasMore = listHasMore.value;
  const error = listError.value;
  const epoch = listEpoch.value;
  const scrollReq = listScrollRequest.value;

  const showInitialSkeleton = loading && items.length === 0;
  // A re-query with cards already on screen (viewport moved, filter changed): keep the
  // old cards readable but visibly provisional — dimmed under a progress bar — until
  // the new set replaces them. Before 2026-09-07 nothing changed on screen for the
  // whole fetch, so a zoom looked ignored and the swap looked like a glitch.
  const updating = listRefetching.value && items.length > 0;

  useEffect(() => {
    scrollListTop();
  }, [epoch]);

  // Explicit "take me to the list" requests (currently the map's city-popup counts). The
  // click that fires this also changes the filters, so wait a frame: the count bar's
  // document position isn't final until the list has re-rendered beneath it. 0 is the
  // never-requested sentinel, so this can't fire on mount.
  useEffect(() => {
    if (!scrollReq) return;
    const id = requestAnimationFrame(scrollListIntoView);
    return () => cancelAnimationFrame(id);
  }, [scrollReq]);

  // Infinite scroll: fetch the next merged chunk when the sentinel nears the viewport.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void loadMoreItems();
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <div class={`concert-list-panel${updating ? " is-updating" : ""}`} aria-busy={updating}>
      {updating && (
        <div class="concert-list-updating" role="status" aria-live="polite">
          <span class="concert-list-updating-bar" aria-hidden="true" />
          <span class="concert-list-updating-label">Updating list…</span>
        </div>
      )}
      {error && (
        <div class="concert-list-empty" role="alert">
          Couldn't load concerts. {error}
        </div>
      )}

      {showInitialSkeleton && (
        <div class="concert-list-loading">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} class="skeleton-card" />
          ))}
        </div>
      )}

      {!showInitialSkeleton && !error && items.length === 0 && (
        <div class="concert-list-empty">
          No events match your filters. Try broadening the time window or re-enabling
          sources.
        </div>
      )}

      <div class="concert-list" role="list">
        {items.map((item) =>
          item.kind === "live" ? (
            <ConcertCard key={`l${item.concert.id}`} concert={item.concert} />
          ) : (
            <HistoricalConcertCard key={`h${item.concert.id}`} concert={item.concert} />
          ),
        )}
      </div>

      <div ref={sentinelRef} style="height: 1px" aria-hidden="true" />

      {items.length > 0 && hasMore && (
        <div class="concert-list-more">
          {loading ? "Loading more…" : `Showing ${items.length.toLocaleString()} — scroll for more`}
        </div>
      )}
    </div>
  );
}
